// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Dataset } from '../data';
import type { Facility } from '../types';
import { OVER, UNDER, positionColour, sectorColour, STATE_NAMES } from '../sectors';
import { donut } from './charts';
import { escapeHtml, formatCompact, formatNumber, formatPercent } from '../utils/format';
import { rankedBy } from '../analysis';
import { glossaryTerm } from '../glossary';

/** Per-facility drill-down drawer. Hash-linkable (#f=slug). */
export function createDrilldown(data: Dataset): { open: (slug: string) => void; close: () => void } {
  const rankedCovered = rankedBy(data.facilities, 'covered');
  const rankedExcess = rankedBy(data.facilities, 'excess');

  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  let drawer: HTMLElement | null = null;

  const close = () => {
    overlay.classList.remove('open');
    if (drawer) {
      drawer.remove(); // detach from the DOM so it can never scroll the page on iOS
      drawer = null;
    }
    if (location.hash.startsWith('#f=')) history.replaceState(null, '', location.pathname + location.search);
  };

  const open = (slug: string) => {
    const f = data.bySlug.get(slug);
    if (!f) return;
    if (drawer) drawer.remove();
    drawer = document.createElement('div');
    drawer.className = 'drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    render(drawer, f);
    document.body.appendChild(drawer);
    // Force reflow before adding .open so the transition runs.
    void drawer.offsetWidth;
    overlay.classList.add('open');
    drawer.classList.add('open');
    (drawer.querySelector('.modal-close') as HTMLElement)?.focus();
    history.replaceState(null, '', `#f=${slug}`);
  };

  function render(root: HTMLElement, f: Facility) {
    const pos = f.excess > 0.5 ? 'over' : f.excess < -0.5 ? 'under' : 'at';
    const posColour = positionColour(f.excess);
    const rank = rankedCovered.findIndex((x) => x.slug === f.slug) + 1;
    const excessRank = rankedExcess.findIndex((x) => x.slug === f.slug) + 1;
    const stateName = STATE_NAMES[f.state] ?? f.state;

    const gasSegs = [
      { label: 'Carbon dioxide', value: f.co2, colour: '#6b7280' },
      { label: 'Methane', value: f.ch4, colour: OVER },
      { label: 'Nitrous oxide', value: f.n2o, colour: '#8b5cf6' },
      { label: 'Other', value: f.ghgOther, colour: '#cbd5e1' },
    ].filter((s) => s.value > 0);

    const cmpMax = Math.max(f.covered, f.baseline) * 1.05 || 1;
    const cmpRow = (label: string, v: number, colour: string) => `
      <div class="compare-row">
        <div class="compare-label">${escapeHtml(label)}</div>
        <div class="compare-track"><div class="compare-fill" style="width:${((v / cmpMax) * 100).toFixed(1)}%;background:${colour}"></div></div>
        <div class="compare-val">${formatCompact(v)}</div>
      </div>`;

    root.innerHTML = `
      <button class="modal-close" aria-label="Close">✕</button>
      <div class="drawer-head">
        <div class="drawer-title">${escapeHtml(f.name)}</div>
        <div class="drawer-sub">
          ${escapeHtml(f.company)}
          · <span class="pill" style="background:${sectorColour(f.sector)}">${escapeHtml(f.sector)}</span>
          ${f.state !== 'MULTI' ? `· ${escapeHtml(stateName)}` : '· National / multi-state'}
        </div>
      </div>
      <div class="drawer-body">
        <div class="drawer-section">
          <div class="kv-grid">
            <div class="kv"><div class="kv-label">Covered emissions</div><div class="kv-value">${formatCompact(f.covered)}<span class="kv-unit"> tCO₂-e</span></div></div>
            <div class="kv"><div class="kv-label">Baseline</div><div class="kv-value">${formatCompact(f.baseline)}<span class="kv-unit"> tCO₂-e</span></div></div>
            <div class="kv"><div class="kv-label">${pos === 'under' ? 'Under baseline' : 'Over baseline'}</div><div class="kv-value" style="color:${posColour}">${f.excess > 0 ? '+' : ''}${formatCompact(f.excess)}</div></div>
            <div class="kv"><div class="kv-label">Methane share</div><div class="kv-value" style="color:${OVER}">${f.methaneShare != null ? formatPercent(f.methaneShare) : '—'}</div></div>
          </div>
          <div class="tag ${pos}">${pos === 'over' ? 'Emitted above its baseline' : pos === 'under' ? 'Emitted below its baseline' : 'At its baseline'}</div>
        </div>

        <div class="drawer-section">
          <h3>Covered emissions vs baseline</h3>
          ${cmpRow('Emitted', f.covered, posColour)}
          ${cmpRow('Baseline (the cap)', f.baseline, '#94a3b8')}
          <p class="drawer-note">
            Ranked <strong>#${rank}</strong> of ${formatNumber(rankedCovered.length)} facilities by covered emissions${
              f.excess > 0 ? `, and <strong>#${excessRank}</strong> by how far over baseline` : ''
            }.
          </p>
        </div>

        <div class="drawer-section" data-role="gas">
          <h3>Greenhouse gas mix</h3>
          <div style="display:flex;gap:var(--space-lg);align-items:center;flex-wrap:wrap">
            <div data-role="gas-svg" style="flex-shrink:0"></div>
            <div class="legend" style="flex-direction:column;gap:4px;margin:0" data-role="gas-legend"></div>
          </div>
        </div>

        <div class="drawer-section">
          <h3>Carbon credits</h3>
          <table class="data" style="font-size:var(--font-size-sm)">
            <tbody>
              <tr style="cursor:default"><td>${glossaryTerm('smc', 'SMCs issued')} <span class="muted">(earned by beating baseline)</span></td><td class="t-num">${formatNumber(f.smcsIssued)}</td></tr>
              <tr style="cursor:default"><td>${glossaryTerm('accu', 'ACCUs surrendered')}</td><td class="t-num">${formatNumber(f.accusSurrendered)}</td></tr>
              <tr style="cursor:default"><td>SMCs surrendered</td><td class="t-num">${formatNumber(f.smcsSurrendered)}</td></tr>
              <tr style="cursor:default"><td><strong>Net position</strong> <span class="muted">(after credits)</span></td><td class="t-num">${f.netPosition != null ? (f.netPosition > 0 ? '+' : '') + formatCompact(f.netPosition) : 'deferred'}</td></tr>
            </tbody>
          </table>
        </div>

        ${
          f.prevCovered != null
            ? `<div class="drawer-section">
                 <h3>Change since ${escapeHtml(data.meta.prevYear)}</h3>
                 <p class="drawer-note">
                   Covered emissions ${(f.coveredChange ?? 0) >= 0 ? 'rose' : 'fell'}
                   <strong style="color:${(f.coveredChange ?? 0) >= 0 ? OVER : UNDER}">${(f.coveredChange ?? 0) >= 0 ? '+' : ''}${formatCompact(f.coveredChange ?? 0)}</strong>
                   (${formatPercent((((f.coveredChange ?? 0) / (f.prevCovered || 1)) * 100))}) from ${formatCompact(f.prevCovered)} tCO₂-e.
                 </p>
               </div>`
            : ''
        }

        ${f.notes && f.notes !== '-' ? `<div class="drawer-section"><h3>Regulator note</h3><p class="drawer-note">${escapeHtml(f.notes)}</p></div>` : ''}
      </div>
    `;

    const gasSlot = root.querySelector('[data-role="gas-svg"]') as HTMLElement;
    gasSlot.appendChild(donut(gasSegs, { size: 150 }));
    const legend = root.querySelector('[data-role="gas-legend"]') as HTMLElement;
    legend.innerHTML = gasSegs
      .map(
        (s) =>
          `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${s.colour}"></span>${escapeHtml(s.label)} ${formatPercent(f.covered > 0 ? (s.value / f.covered) * 100 : 0)}</span>`,
      )
      .join('');

    root.querySelector('.modal-close')?.addEventListener('click', close);
  }

  document.body.appendChild(overlay);
  overlay.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer) close();
  });

  return { open, close };
}
